import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PIPELINE_STAGES, PORTFOLIO, IN_DEVELOPMENT } from "../data/content";

/* ═══════════════════════════════════════════════════════════════
   Apple-style Mega Nav
   - Slim (~48px) translucent bar
   - Center-aligned menu, thin sans-serif
   - Hover mega-menu drawer (top-anchored, full-width)
   - Mobile: full-screen sheet with expandable groups
   ═══════════════════════════════════════════════════════════════ */

const MENUS = [
  {
    id: "studio",
    label: "Studio",
    href: "#services",
    panel: {
      lead: {
        eyebrow: "The Studio",
        title: "One roof. Twelve departments. Every discipline.",
        body: "From treatment to DCP — an end-to-end animation house engineered for producers who need to hit a number and a look at the same time.",
        cta: { label: "See the Cost Advantage", href: "#services" },
      },
      groups: [
        {
          heading: "What We Do",
          items: [
            { label: "Animation Production", desc: "Features & series, end-to-end", href: "#services" },
            { label: "Original IP", desc: "Development slate available for co-pro", href: "#originals" },
            { label: "Global Distribution", desc: "SVOD · AVOD · TVOD · territorial", href: "#ott" },
            { label: "Why VawWorld", desc: "Four reasons producers stop looking", href: "#why" },
          ],
        },
        {
          heading: "The Numbers",
          items: [
            { label: "Cost Advantage", desc: "40–60% below US / European quotes", href: "#services" },
            { label: "VawWorldOS", desc: "Live producer console · request a demo", href: "#vawworldos" },
            { label: "$2M / 90-min Feature", desc: "Read the playbook", href: "#insights" },
            { label: "Pipeline", desc: "12 stages, one studio", href: "#pipeline" },
          ],
        },
      ],
    },
  },
  {
    id: "work",
    label: "Work",
    href: "#portfolio",
    panel: {
      lead: {
        eyebrow: "Delivered · 2015 — 2024",
        title: "A shelf we're happy to walk you through.",
        body: "Features, series and short-form work — delivered to broadcasters, streamers and international clients across four continents.",
        cta: { label: "Open the Full Portfolio", href: "#portfolio" },
      },
      thumbnails: PORTFOLIO.slice(0, 4).map((p) => ({
        label: p.title, desc: p.format, href: "#portfolio", thumb: p.thumb,
      })),
    },
  },
  {
    id: "pipeline",
    label: "Pipeline",
    href: "#pipeline",
    panel: {
      lead: {
        eyebrow: "How It Ships",
        title: "Screenplay to DCP. One team. One P&L.",
        body: "A locked, department-by-department pipeline built for feature-scale scrutiny. Blender · Maya · Houdini · Unreal · Nuke — under one supervisor.",
        cta: { label: "See the Pipeline", href: "#pipeline" },
      },
      // 12 stages in 3 groups of 4
      groups: [
        {
          heading: "Story · Design",
          items: PIPELINE_STAGES.slice(0, 4).map((s) => ({ label: s.title, desc: s.body.slice(0, 62) + "…", href: "#pipeline" })),
        },
        {
          heading: "Production · Lighting",
          items: PIPELINE_STAGES.slice(4, 8).map((s) => ({ label: s.title, desc: s.body.slice(0, 62) + "…", href: "#pipeline" })),
        },
        {
          heading: "Post · Delivery",
          items: PIPELINE_STAGES.slice(8, 12).map((s) => ({ label: s.title, desc: s.body.slice(0, 62) + "…", href: "#pipeline" })),
        },
      ],
    },
  },
  {
    id: "reel",
    label: "Reel",
    href: "#showreel",
  },
  {
    id: "originals",
    label: "Originals",
    href: "#originals",
    panel: {
      lead: {
        eyebrow: "Development Slate · Territories Open",
        title: "Worlds we're building.",
        body: "Original animated properties in active development. Available for co-production, licensing and territorial acquisition.",
        cta: { label: "Request the Slate Deck", href: "#contact" },
      },
      thumbnails: IN_DEVELOPMENT.map((p) => ({
        label: p.title, desc: p.format, href: "#originals", thumb: p.thumb,
      })),
    },
  },
  {
    id: "insights",
    label: "Insights",
    href: "#insights",
    panel: {
      lead: {
        eyebrow: "The Insights Desk",
        title: "The trade press we actually read.",
        body: "Original playbooks from our floor, plus a curated feed of industry news from Variety, THR, Cartoon Brew and more — with markets calendar built in.",
        cta: { label: "Open the Magazine", href: "#magazine" },
      },
      groups: [
        {
          heading: "Playbooks",
          items: [
            { label: "$2M / 90-Min Feature", desc: "The math behind the headline", href: "/case-study/2m-feature" },
            { label: "Unreal in the Pipeline", desc: "Where real-time actually wins", href: "#insights" },
            { label: "India Co-Production Guide", desc: "Practical mechanics for producers", href: "#insights" },
            { label: "60% Asset Re-use", desc: "Not cheating — craftsmanship", href: "#insights" },
          ],
        },
        {
          heading: "Markets · Meet Us",
          items: [
            { label: "Kidscreen Summit", desc: "Miami · Feb 09 – 12, 2026", href: "#magazine" },
            { label: "Annecy · MIFA", desc: "Annecy · Jun 08 – 13, 2026", href: "#magazine" },
            { label: "MIPCOM · MIPJunior", desc: "Cannes · Oct 10 – 15, 2026", href: "#magazine" },
            { label: "AFM · Los Angeles", desc: "Nov 04 – 08, 2026", href: "#magazine" },
          ],
        },
      ],
    },
  },
  {
    id: "contact",
    label: "Contact",
    href: "#contact",
  },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenId(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const openMenu = (id) => {
    clearTimeout(closeTimer.current);
    setOpenId(id);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenId(null), 150);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  const activeMenu = MENUS.find((m) => m.id === openId && m.panel);

  return (
    <>
      {/* NAV BAR */}
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        onMouseLeave={scheduleClose}
        className="fixed top-0 left-0 right-0 z-50"
        data-testid="vw-nav"
        style={{
          backdropFilter: "saturate(180%) blur(20px)",
          WebkitBackdropFilter: "saturate(180%) blur(20px)",
          background: scrolled ? "rgba(5,7,12,0.72)" : "rgba(5,7,12,0.55)",
          borderBottom: openId ? "1px solid transparent" : "1px solid rgba(255,255,255,0.06)",
          transition: "background 400ms ease, border-color 300ms ease",
        }}
      >
        <div className="max-w-[1560px] mx-auto px-6 md:px-10">
          <div className="h-12 md:h-[52px] flex items-center justify-between">
            {/* Logo */}
            <a
              href="#top"
              className="flex items-center gap-2.5 shrink-0"
              onMouseEnter={scheduleClose}
              data-testid="vw-logo"
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 32 32"
                fill="none"
                aria-label="VawWorld"
                style={{ filter: "drop-shadow(0 0 10px rgba(0,229,255,0.45))" }}
                className="shrink-0"
              >
                <defs>
                  <linearGradient id="vwLogoGrad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#00E5FF"/>
                    <stop offset="100%" stopColor="#2B6CFF"/>
                  </linearGradient>
                </defs>
                {/* outer diamond frame */}
                <path d="M16 1.5 L30.5 16 L16 30.5 L1.5 16 Z" stroke="url(#vwLogoGrad)" strokeWidth="1.4" fill="rgba(0,229,255,0.04)"/>
                {/* V mark */}
                <path d="M8.5 10.5 L13.5 22 L15.4 22 L11 10.5 Z" fill="url(#vwLogoGrad)"/>
                {/* W mark */}
                <path d="M15.4 22 L17.3 22 L20.2 15.2 L23.1 22 L25 22 L20.2 10.5 L14.8 22 Z" fill="url(#vwLogoGrad)" opacity="0.85"/>
                {/* accent dot */}
                <circle cx="16" cy="26.6" r="1" fill="#00E5FF"/>
              </svg>
              <span className="font-display text-[15px] tracking-tight text-[var(--vw-text)]">
                VawWorld
              </span>
            </a>

            {/* Center — Apple-style menu */}
            <nav
              className="hidden lg:flex items-center gap-2"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              data-testid="vw-nav-menu"
            >
              {MENUS.map((m) => (
                <a
                  key={m.id}
                  href={m.href}
                  onMouseEnter={() => (m.panel ? openMenu(m.id) : setOpenId(null))}
                  onFocus={() => (m.panel ? openMenu(m.id) : setOpenId(null))}
                  onClick={() => setOpenId(null)}
                  data-testid={`vw-nav-${m.id}`}
                  className={`px-3.5 py-2 text-[12.5px] font-medium tracking-tight rounded-md transition-colors ${
                    openId === m.id ? "text-[var(--vw-cyan)]" : "text-[var(--vw-text-dim)] hover:text-[var(--vw-text)]"
                  }`}
                >
                  {m.label}
                </a>
              ))}
            </nav>

            {/* Right: search + CTA */}
            <div className="flex items-center gap-1">
              <a
                href="/estimator"
                onMouseEnter={scheduleClose}
                data-testid="vw-nav-cta"
                className="hidden md:inline-flex items-center gap-1.5 h-[30px] px-3.5 rounded-full bg-[var(--vw-cyan)] text-black text-[11.5px] font-semibold tracking-tight hover:bg-white transition-colors"
              >
                Get a Budget
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden w-9 h-9 grid place-items-center text-[var(--vw-text)] hover:text-[var(--vw-cyan)]"
                aria-label="Menu"
                data-testid="vw-nav-menu-toggle"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  {mobileOpen ? <path d="M6 6l12 12M6 18L18 6"/> : (<><path d="M4 8h16"/><path d="M4 16h16"/></>)}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* MEGA-MENU PANEL */}
        <AnimatePresence>
          {activeMenu && (
            <motion.div
              key={activeMenu.id}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              className="absolute left-0 right-0 top-full border-t border-[var(--vw-border)]"
              style={{
                backdropFilter: "saturate(180%) blur(24px)",
                WebkitBackdropFilter: "saturate(180%) blur(24px)",
                background: "rgba(5,7,12,0.94)",
              }}
              data-testid={`vw-nav-panel-${activeMenu.id}`}
            >
              <MegaPanel panel={activeMenu.panel} onSelect={() => setOpenId(null)} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* MOBILE SHEET */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-x-0 top-12 z-40 lg:hidden border-b border-[var(--vw-border)]"
            style={{
              backdropFilter: "saturate(180%) blur(20px)",
              WebkitBackdropFilter: "saturate(180%) blur(20px)",
              background: "rgba(5,7,12,0.96)",
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
            }}
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {MENUS.map((m) => (
                <a
                  key={m.id}
                  href={m.href}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 border-b border-[var(--vw-border)] flex items-center justify-between font-display text-lg text-[var(--vw-text)] hover:text-[var(--vw-cyan)]"
                >
                  {m.label}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </a>
              ))}
              <a
                href="/estimator"
                onClick={() => setMobileOpen(false)}
                className="mt-6 vw-btn-primary self-start"
              >
                Get a Budget Estimate
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* MegaPanel — the drop-down content */
function MegaPanel({ panel, onSelect }) {
  const { lead, groups, thumbnails } = panel;
  return (
    <div className="max-w-[1560px] mx-auto px-6 md:px-10 py-10 md:py-12">
      <div className="grid md:grid-cols-[1.15fr_2fr] gap-10 md:gap-16">
        {/* Lead */}
        <div className="max-w-md">
          {lead?.eyebrow && (
            <div className="vw-eyebrow mb-4" style={{ fontSize: "10px" }}>
              {lead.eyebrow}
            </div>
          )}
          {lead?.title && (
            <h3 className="font-display text-2xl md:text-[28px] leading-[1.05] tracking-tight text-[var(--vw-text)] mb-4">
              {lead.title}
            </h3>
          )}
          {lead?.body && (
            <p className="text-[13.5px] leading-relaxed text-[var(--vw-text-dim)] mb-6">
              {lead.body}
            </p>
          )}
          {lead?.cta && (
            <a
              href={lead.cta.href}
              onClick={onSelect}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-[var(--vw-cyan)] hover:text-white transition-colors group"
            >
              {lead.cta.label}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </a>
          )}
        </div>

        {/* Right area */}
        {groups && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-8">
            {groups.map((g) => (
              <div key={g.heading}>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-4 pb-2 border-b border-[var(--vw-border)]">
                  {g.heading}
                </div>
                <ul className="space-y-3.5">
                  {g.items.map((it) => (
                    <li key={it.label}>
                      <a
                        href={it.href}
                        onClick={onSelect}
                        className="group flex items-start gap-3 -mx-2 px-2 py-1.5 rounded hover:bg-white/[0.04] transition-colors"
                      >
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-[var(--vw-cyan)] shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                        <span>
                          <span className="block text-[13.5px] font-medium text-[var(--vw-text)] group-hover:text-[var(--vw-cyan)] transition-colors">
                            {it.label}
                          </span>
                          {it.desc && (
                            <span className="block text-[11.5px] text-[var(--vw-text-mute)] mt-0.5 leading-relaxed">
                              {it.desc}
                            </span>
                          )}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {thumbnails && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {thumbnails.map((t) => (
              <a
                href={t.href}
                onClick={onSelect}
                key={t.label}
                className="group block"
              >
                <div className="aspect-[3/4] overflow-hidden bg-black border border-[var(--vw-border)] group-hover:border-[var(--vw-cyan)] transition-colors">
                  <img
                    src={t.thumb}
                    alt={t.label}
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="mt-2 text-[13.5px] font-medium text-[var(--vw-text)] group-hover:text-[var(--vw-cyan)] transition-colors">
                  {t.label}
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--vw-text-mute)] font-mono mt-0.5">
                  {t.desc}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
